// @catalog F.047
// Bloque "Pedidos" de /mercado-libre (CR-3/CR-5): filtros mes + estatus +
// solo-sin-match, tabla paginada con el desglose por pedido, costo Odoo y
// ambos márgenes. La tasa de match del filtro se muestra junto a los filtros;
// los pedidos sin match aparecen marcados y jamás inventan costo.
import { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  BodyCell,
  BodyRow,
  HeaderCell,
  TableShell,
} from "../analytics/TableCells";
import MercadoLibreImportService, {
  type MlOperationStatus,
} from "../../services/mercadoLibreImport.service";

const fmt = MercadoLibreImportService.formatCurrency;
const PAGE_SIZE = 50;

const STATUS_LABEL: Record<MlOperationStatus, string> = {
  preparacion: "Preparación",
  en_camino: "En camino",
  entregado: "Entregado",
  devuelto_reembolsado: "Devuelto/Reembolsado",
  cancelado: "Cancelado",
  desconocido: "Desconocido",
};

const STATUS_COLOR: Record<
  MlOperationStatus,
  "default" | "info" | "success" | "warning" | "error"
> = {
  preparacion: "info",
  en_camino: "warning",
  entregado: "success",
  devuelto_reembolsado: "error",
  cancelado: "default",
  desconocido: "default",
};

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

export function PedidosBlock(): JSX.Element {
  // null = todavía sin decidir: al cargar, el default es el último mes CON
  // datos (un import suele traer meses pasados y el mes actual salía vacío).
  const [month, setMonth] = useState<string | null>(null);
  const [status, setStatus] = useState<MlOperationStatus | "">("");
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [page, setPage] = useState(1);

  const monthsQuery = useQuery({
    queryKey: ["ml-summary-monthly"],
    queryFn: () => MercadoLibreImportService.getMonthlySummary(),
  });
  useEffect(() => {
    if (month === null && monthsQuery.data) {
      setMonth(monthsQuery.data[0]?.month ?? currentMonth());
    }
  }, [month, monthsQuery.data]);

  const ordersQuery = useQuery({
    queryKey: ["ml-orders", month, status, unmatchedOnly, page],
    queryFn: () =>
      MercadoLibreImportService.getOrders({
        month: month ?? "",
        status: status || undefined,
        unmatched: unmatchedOnly || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: month !== null && /^\d{4}-\d{2}$/.test(month),
  });
  const data = ordersQuery.data;
  const pageCount = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        useFlexGap
        sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
      >
        <Typography
          component="h2"
          sx={{ fontSize: "16px", fontWeight: 600, color: "var(--ink-1)" }}
        >
          Pedidos
        </Typography>
        <FormControlLabel
          sx={{ ml: 0.5 }}
          control={
            <Checkbox
              size="small"
              checked={unmatchedOnly}
              onChange={(e) => {
                setUnmatchedOnly(e.target.checked);
                setPage(1);
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: "13px" }}>Solo sin match</Typography>
          }
        />
        <Box sx={{ flex: 1 }} />
        {data && (
          <Typography sx={{ fontSize: "12.5px", color: "var(--ink-3)" }}>
            {data.total.toLocaleString()} pedidos · match Odoo{" "}
            {Math.round(data.matchRate * 100)}% ·{" "}
            {data.unmatchedCount.toLocaleString()} sin match
          </Typography>
        )}
        <TextField
          size="small"
          type="month"
          label="Mes"
          value={month ?? ""}
          onChange={(e) => {
            setMonth(e.target.value);
            setPage(1);
          }}
          sx={{ width: 170 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          select
          label="Estatus"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as MlOperationStatus | "");
            setPage(1);
          }}
          sx={{ width: 210 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {Object.entries(STATUS_LABEL).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <TableShell maxHeight="max(360px, calc(100vh - 300px))">
        <Box component="thead">
          <Box component="tr">
            <HeaderCell>Pedido</HeaderCell>
            <HeaderCell>Fecha</HeaderCell>
            <HeaderCell>Estatus</HeaderCell>
            <HeaderCell align="right">Bruto</HeaderCell>
            <HeaderCell align="right">Comisión</HeaderCell>
            <HeaderCell align="right">Retenciones</HeaderCell>
            <HeaderCell align="right">Envío</HeaderCell>
            <HeaderCell align="right">Reembolso</HeaderCell>
            <HeaderCell align="right">Neto</HeaderCell>
            <HeaderCell align="right">Costo Odoo</HeaderCell>
            <HeaderCell align="right">Margen real</HeaderCell>
          </Box>
        </Box>
        <Box component="tbody">
          {(data?.rows ?? []).length === 0 && (
            <Box component="tr">
              <BodyCell muted>
                {ordersQuery.isLoading
                  ? "Cargando…"
                  : "Sin pedidos para el filtro."}
              </BodyCell>
            </Box>
          )}
          {(data?.rows ?? []).map((r) => (
            <BodyRow key={`${r.orderId}|${r.saleId}`}>
              <BodyCell>
                <Tooltip
                  arrow
                  title={`# venta ${r.saleId || "—"}${r.statusRaw ? ` · "${r.statusRaw}"` : ""}`}
                >
                  <span>{r.orderId}</span>
                </Tooltip>
              </BodyCell>
              <BodyCell muted>{r.saleDate}</BodyCell>
              <BodyCell>
                <Chip
                  size="small"
                  label={STATUS_LABEL[r.status]}
                  color={STATUS_COLOR[r.status]}
                  variant="outlined"
                />
              </BodyCell>
              <BodyCell align="right">{fmt(r.grossProduct)}</BodyCell>
              <BodyCell align="right">{fmt(r.commissionPure)}</BodyCell>
              <BodyCell align="right">
                {fmt(r.withholdingIva + r.withholdingIsr)}
              </BodyCell>
              <BodyCell align="right">{fmt(r.shipNet)}</BodyCell>
              <BodyCell align="right">{fmt(r.refunds)}</BodyCell>
              <BodyCell align="right">{fmt(r.totalReceived)}</BodyCell>
              <BodyCell align="right" muted={r.odooCostTotal === null}>
                {r.odooCostTotal === null ? (
                  <Chip size="small" label="Sin match" variant="outlined" />
                ) : (
                  fmt(r.odooCostTotal)
                )}
              </BodyCell>
              <BodyCell align="right" muted={r.marginReal === null}>
                {fmt(r.marginReal)}
              </BodyCell>
            </BodyRow>
          ))}
        </Box>
      </TableShell>
      {pageCount > 1 && (
        <Stack alignItems="center" sx={{ mt: 1.5 }}>
          <Pagination
            size="small"
            count={pageCount}
            page={page}
            onChange={(_e, p) => setPage(p)}
          />
        </Stack>
      )}
    </Box>
  );
}
